/**
 * Environment Configuration Service
 *
 * Loads reviewed runtime configuration for deployed immutable bundles.
 *
 * For local development: Create .env.local file from .env.example
 * For deployed release bundles: publish /runtime-config.json from the reviewed
 * candidate-bound runtime-configuration package.
 */

import { normalizeCouncilValue } from '../utils/council.js';

/**
 * Default configuration values (fallbacks)
 */
const DEFAULT_CONFIG = {
    environment: 'prod',
    portalUrl: 'https://assist.myenterprise.ai',
    apiBaseUrl: '/api'
};
const RUNTIME_CONFIG_SCHEMA = 'eai.website_runtime_configuration.v1';
const SOURCE_SHA_PATTERN = /^[0-9a-f]{40}(?:[0-9a-f]{24})?$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const WORKFLOW_ID_PATTERN = /^[0-9a-f]{24}$/;
const RUNTIME_CONFIG_REQUIRED =
    typeof __EAI_RUNTIME_CONFIG_REQUIRED__ !== 'undefined' && __EAI_RUNTIME_CONFIG_REQUIRED__;

/**
 * Parse the local-development council mapping.
 * @returns {Array} - Array of council objects
 */
function parseCouncilMapping(mapping) {
    try {
        if (Array.isArray(mapping)) return mapping;
        if (mapping) return JSON.parse(mapping);
    } catch (error) {
        console.error('Failed to parse VITE_COUNCIL_MAPPING:', error);
    }
    return [];
}

function localRuntimeConfiguration() {
    return {
        schema: RUNTIME_CONFIG_SCHEMA,
        environment: import.meta.env.VITE_ENVIRONMENT || DEFAULT_CONFIG.environment,
        source_sha: 'local',
        release_tag: 'local',
        portalUrl: import.meta.env.VITE_PORTAL_URL || DEFAULT_CONFIG.portalUrl,
        apiBaseUrl: DEFAULT_CONFIG.apiBaseUrl,
        emailTemplateURL: import.meta.env.VITE_EMAIL_TEMPLATE_URL,
        bushfirePractitionersURL:
            import.meta.env.VITE_BUSHFIRE_PRACTITIONERS_URL ||
            'https://www.fpaa.com.au/Web/Registers/BPAD-Accredited-Practitioner-Register/Web/Registers/BPAD_register.aspx',
        heritageConsultantsURL:
            import.meta.env.VITE_HERITAGE_CONSULTANTS_URL ||
            'https://www.environment.nsw.gov.au/topics/heritage/manage-heritage-items/heritage-consultants-directory',
        heritageMoreInfoBaseURL:
            import.meta.env.VITE_HERITAGE_MORE_INFO_BASE_URL ||
            'https://www.hms.heritage.nsw.gov.au/App/Item/ViewItem?itemId=',
        councilTable: parseCouncilMapping(import.meta.env.VITE_COUNCIL_MAPPING)
    };
}

export function validateRuntimeConfiguration(payload) {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        throw new Error('Runtime configuration must be an object');
    }
    if (payload.schema !== RUNTIME_CONFIG_SCHEMA) {
        throw new Error(`Runtime configuration schema must be ${RUNTIME_CONFIG_SCHEMA}`);
    }
    if (!['dev', 'test', 'prod'].includes(payload.environment)) {
        throw new Error('Runtime configuration environment is invalid');
    }
    for (const field of [
        'source_sha',
        'release_tag',
        'portalUrl',
        'apiBaseUrl',
        'emailTemplateURL',
        'bushfirePractitionersURL',
        'heritageConsultantsURL',
        'heritageMoreInfoBaseURL'
    ]) {
        if (typeof payload[field] !== 'string' || !payload[field].trim()) {
            throw new Error(`Runtime configuration ${field} is required`);
        }
    }
    if (!SOURCE_SHA_PATTERN.test(payload.source_sha)) {
        throw new Error('Runtime configuration source_sha is invalid');
    }
    if (!/^v[0-9][A-Za-z0-9._-]*$/.test(payload.release_tag)) {
        throw new Error('Runtime configuration release_tag is invalid');
    }
    if (payload.apiBaseUrl !== '/api') {
        throw new Error('Runtime configuration apiBaseUrl must use the same-origin proxy');
    }
    for (const field of [
        'portalUrl',
        'emailTemplateURL',
        'bushfirePractitionersURL',
        'heritageConsultantsURL',
        'heritageMoreInfoBaseURL'
    ]) {
        if (new URL(payload[field]).protocol !== 'https:') {
            throw new Error(`Runtime configuration ${field} must use https`);
        }
    }
    if (!Array.isArray(payload.councilTable) || payload.councilTable.length === 0) {
        throw new Error('Runtime configuration councilTable must be a non-empty array');
    }
    const names = new Set();
    for (const council of payload.councilTable) {
        const name = typeof council?.name === 'string' ? council.name.trim() : '';
        if (
            !name ||
            names.has(name.toLowerCase()) ||
            !UUID_PATTERN.test(council.entra_id) ||
            !UUID_PATTERN.test(council.tenant_id) ||
            !(WORKFLOW_ID_PATTERN.test(council.workflow_id) || UUID_PATTERN.test(council.workflow_id))
        ) {
            throw new Error('Runtime configuration contains an invalid council binding');
        }
        if (
            council.genesys &&
            (!/^[a-z0-9.-]+$/.test(council.genesys.region) || !UUID_PATTERN.test(council.genesys.deployment_id))
        ) {
            throw new Error('Runtime configuration contains an invalid Genesys binding');
        }
        names.add(name.toLowerCase());
    }
    return payload;
}

function getLocalDevFallbackConfig() {
    if (!import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT !== 'dev') {
        return {
            tenantId: null,
            workflowId: null
        };
    }

    return {
        tenantId: import.meta.env.VITE_TENANT_ID || null,
        workflowId: import.meta.env.VITE_WORKFLOW_ID || null
    };
}

/**
 * ConfigService class - manages application configuration
 * Deployed releases read reviewed runtime configuration; local/test builds use
 * Vite variables for backward-compatible developer ergonomics.
 */
export class ConfigService {
    constructor({ runtimeConfigRequired = RUNTIME_CONFIG_REQUIRED, fetchImpl } = {}) {
        this._config = null;
        this._environment = null;
        this._isLoaded = false;
        this._runtimeConfigRequired = runtimeConfigRequired;
        // fetch() must be called with a Window receiver. Storing globalThis.fetch
        // unbound and invoking it as this._fetch(...) passes this ConfigService as
        // the receiver, which browsers reject with "Illegal invocation" — throwing
        // before any request is made, so the widget never boots on a deployed build.
        this._fetch = fetchImpl ?? globalThis.fetch?.bind(globalThis);
    }

    async _loadConfigurationSource() {
        if (!this._runtimeConfigRequired) return localRuntimeConfiguration();
        if (typeof this._fetch !== 'function') {
            throw new Error('Runtime configuration fetch is unavailable');
        }
        const response = await this._fetch('/runtime-config.json', {
            cache: 'no-store',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
        });
        if (!response.ok) {
            throw new Error(`Runtime configuration request failed with HTTP ${response.status}`);
        }
        return validateRuntimeConfiguration(await response.json());
    }

    /**
     * Load configuration from environment variables
     * @returns {Promise<Object>} - Loaded configuration
     */
    async load() {
        if (this._isLoaded) {
            return this._config;
        }

        try {
            const source = await this._loadConfigurationSource();
            this._environment = source.environment;

            this._config = {
                environment: this._environment,
                portalUrl: source.portalUrl,
                apiBaseUrl: source.apiBaseUrl,
                TENANT_ID: null,
                WORKFLOW_ID: null,
                emailTemplateURL: source.emailTemplateURL,
                bushfirePractitionersURL: source.bushfirePractitionersURL,
                heritageConsultantsURL: source.heritageConsultantsURL,
                heritageMoreInfoBaseURL: source.heritageMoreInfoBaseURL,
                councilTable: source.councilTable,
                mappingTable: source.councilTable
            };

            // Runtime council resolution: the active ?Council= value is the
            // source of truth for tenant/workflow routing. If no mapping exists,
            // leave TENANT_ID unset so the missing mapping is visible.
            const activeCouncilName = this.getCouncilFromUrl();
            const councilConfig = this.resolveCouncilConfig(activeCouncilName);
            if (councilConfig?.tenant_id && councilConfig?.workflow_id) {
                this._config.TENANT_ID = councilConfig.tenant_id;
                this._config.WORKFLOW_ID = councilConfig.workflow_id;
            } else if (!activeCouncilName) {
                const localDevFallback = getLocalDevFallbackConfig();
                this._config.TENANT_ID = localDevFallback.tenantId;
                this._config.WORKFLOW_ID = localDevFallback.workflowId;
            }

            this._isLoaded = true;

            // Expose mappingTable to window for council validation
            if (typeof window !== 'undefined') {
                window.mappingTable = this._config.mappingTable;
                window.__EAI_RUNTIME_ENVIRONMENT__ = this._environment;
            }

            // Log only in non-prod environments
            if (this._environment !== 'prod') {
                console.log(`Configuration loaded for environment: ${this._environment}`);
            }

            return this._config;
        } catch (error) {
            console.error('❌ Failed to load configuration:', error);
            if (this._runtimeConfigRequired) {
                this._config = null;
                this._environment = null;
                this._isLoaded = false;
                throw new Error('Required runtime configuration is unavailable', {
                    cause: error
                });
            }
            // Return defaults on error
            this._config = { ...DEFAULT_CONFIG };
            this._environment = DEFAULT_CONFIG.environment;
            this._isLoaded = true;
            return this._config;
        }
    }

    /**
     * Get a configuration value
     * @param {string} key - Configuration key
     * @param {*} defaultValue - Default value if key not found
     * @returns {*} - Configuration value
     */
    get(key, defaultValue = null) {
        if (!this._isLoaded) {
            console.warn('Configuration not loaded yet. Call load() first.');
            return defaultValue;
        }
        return this._config[key] ?? defaultValue;
    }

    // ==================== API Endpoints ====================
    // These now use the serverless proxy functions

    /**
     * Get API base URL (proxied through serverless)
     * @returns {string}
     */
    getApiBaseUrl() {
        return this.get('apiBaseUrl', '/api');
    }

    /**
     * Get anonymous token endpoint (serverless proxy)
     * @returns {string}
     */
    getAnonTokenUrl() {
        return `${this.getApiBaseUrl()}/auth/token`;
    }

    // ==================== Council Configuration ====================

    /**
     * Resolve council-specific config (tenant_id, workflow_id, entra_id) from
     * the active runtime council table.
     * @param {string} [councilName] - Active council name from the current embed context
     * @returns {Object|null} - Council config object or null if not found
     */
    resolveCouncilConfig(councilName = this.getCouncilFromUrl()) {
        if (!councilName) return null;

        const mapping = this._config?.councilTable || [];
        return mapping.find((c) => c.name && c.name.toLowerCase() === councilName.toLowerCase()) || null;
    }

    /**
     * Resolve the active council's Genesys live-agent config from the runtime table.
     *
     * Strict by design: returns null when the council has no `genesys` block,
     * so a council without a live-agent queue gets no handover (the caller
     * surfaces an "unavailable" message rather than opening a dead socket).
     * `region` + `deployment_id` are the only fields used to connect today;
     * `organization_id`/`queue_id`/`schedule_id` are carried through for the
     * planned server-side token proxy but are not consumed yet.
     *
     * @param {string} [councilName] - Active council name from the embed context
     * @returns {{region: string, deploymentId: string, organizationId: string|null, queueId: string|null, scheduleId: string|null}|null}
     */
    getGenesysConfig(councilName = this.getCouncilFromUrl()) {
        const genesys = this.resolveCouncilConfig(councilName)?.genesys;
        if (!genesys?.region || !genesys?.deployment_id) return null;
        return {
            region: genesys.region,
            deploymentId: genesys.deployment_id,
            organizationId: genesys.organization_id ?? null,
            queueId: genesys.queue_id ?? null,
            scheduleId: genesys.schedule_id ?? null
        };
    }

    /**
     * Get council name from current embed context, URL parameters, or session storage
     * @returns {string} - Council name
     */
    getCouncilFromUrl() {
        // Every return path is normalized: a CMS-mangled embed can wrap the
        // council in smart/curly quotes (e.g. '“Newcastle"'), which would
        // otherwise miss resolveCouncilConfig()'s name match and fall back to the
        // default tenant. Stripping wrapping quotes/whitespace recovers the name.

        // Check window.DAISY_CONFIG first (set by eaidev.html)
        if (typeof window !== 'undefined' && window.DAISY_CONFIG?.council) {
            return normalizeCouncilValue(window.DAISY_CONFIG.council);
        }

        // Check URL parameters before persisted storage so a stale previous
        // council cannot route the current embed to the wrong tenant.
        if (typeof window !== 'undefined' && window.location) {
            const urlParams = new URLSearchParams(window.location.search);
            const urlCouncil = urlParams.get('Council') || urlParams.get('council');
            if (urlCouncil) return normalizeCouncilValue(urlCouncil);
        }

        // Check session storage only when the current embed context is absent.
        if (typeof sessionStorage !== 'undefined') {
            const stored = sessionStorage.getItem('daisy_council');
            if (stored) return normalizeCouncilValue(stored);
        }

        return '';
    }
}

// Export singleton instance
export const configService = new ConfigService();
